import {
	Button,
	Currency,
	Divider,
	Form,
	Grid,
	Header,
	Input,
	Radio,
	Segment,
} from '@mairistem/composants';
import type { UseServiceProps } from '@mairistem/comptabilite';
import { Combobox, Date, Icon } from '@mairistem/finances-utils';
import React from 'react';
import STATUT_PROJET from '../../../constants/common/statutProjet';
import type Composant from '../../../interfaces/common/composant';
import type Projet from '../../../interfaces/common/projet';
import { renderLabel } from '../../../utils/autorisation';
import CodeInput from './CodeInput';

interface InformationsGeneralesProps {
	autorisation: Projet;
	EtiquettesInput: any;
	hasEvenements: boolean;
	isServiceComboboxVisible: boolean;
	onAddComposant: () => void;
	onChangeAutorisation: (data: Partial<Projet>) => void;
	onChangeComposant: (
		identifiantComposant: number | string,
		data: Partial<Composant>,
	) => void;
	onChangeHasEvenements: () => void;
	onRemoveComposant: (identifiantComposant: number | string) => void;
	ServiceCombobox: (props: UseServiceProps) => React.JSX.Element;
}

const STATUS_OPTIONS = [
	{
		content: () => renderLabel(STATUT_PROJET.BROUILLON),
		key: STATUT_PROJET.BROUILLON,
		text: 'Brouillon',
		value: STATUT_PROJET.BROUILLON,
	},
	{
		content: () => renderLabel(STATUT_PROJET.EN_COURS),
		key: STATUT_PROJET.EN_COURS,
		text: 'En cours',
		value: STATUT_PROJET.EN_COURS,
	},
	{
		content: () => renderLabel(STATUT_PROJET.SOLDE),
		key: STATUT_PROJET.SOLDE,
		text: 'Soldée',
		value: STATUT_PROJET.SOLDE,
	},
];

const InformationsGenerales = ({
	autorisation,
	EtiquettesInput,
	hasEvenements,
	isServiceComboboxVisible,
	onAddComposant,
	onChangeAutorisation,
	onChangeComposant,
	onChangeHasEvenements,
	onRemoveComposant,
	ServiceCombobox,
}: InformationsGeneralesProps) => {
	const handleSelectService = (identifiantService: number) => {
		onChangeAutorisation({ identifiantService });
	};

	const handleBlurComposantInput = (
		{ target: { name, value } }: React.FocusEvent<HTMLInputElement>,
		identifiantComposant: number | string,
	) => {
		onChangeComposant(identifiantComposant, { [name]: value });
	};

	const handleBlurComposantMontant = (
		identifiantComposant: number | string,
		montant: number,
	) => {
		onChangeComposant(identifiantComposant, { montantEvenement: montant });
	};

	const handleChangeAutorisationInput = ({
		target: { name, value },
	}: React.ChangeEvent<HTMLInputElement>) => {
		onChangeAutorisation({ [name]: value });
	};

	const handleChangeAutorisationMontant = (
		{ target: { name } }: React.ChangeEvent<HTMLInputElement>,
		montant: number,
	) => {
		onChangeAutorisation({ [name]: montant });
	};

	const handleChangeDate = (value: string, name: string) => {
		onChangeAutorisation({ [name]: value });
	};

	const handleChangeEtiquette = ({ etiquettes }: { etiquettes: number }) => {
		onChangeAutorisation({ identifiantEtiquette: etiquettes });
	};

	const handleChangeStatut = (
		_e: React.SyntheticEvent,
		{ value }: { value: number },
	) => {
		onChangeAutorisation({ statut: value });
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de 'ServiceCombobox'
	const ServiceInput = React.useMemo(
		() => (
			<>
				<ServiceCombobox
					data-testid="serviceCombobox"
					disabled={!isServiceComboboxVisible}
					identifiantService={autorisation.identifiantService || null}
					onSelect={handleSelectService}
				/>
				<Header
					floated="left"
					size="tiny"
					subheader="Service gestionnaire du suivi de l'autorisation"
				/>
			</>
		),
		[autorisation.identifiantService, isServiceComboboxVisible],
	);

	return (
		<>
			<Form>
				<Grid>
					<Grid.Row>
						<Grid.Column textAlign="center" verticalAlign="middle" width={2}>
							<Combobox
								onChange={handleChangeStatut}
								// @ts-expect-error on veut du JSX.Element pas du ReactNode
								options={STATUS_OPTIONS}
								// @ts-expect-error on veut du JSX.Element pas un string
								text={() => renderLabel(autorisation.statut)}
								value={autorisation.statut}
							/>
						</Grid.Column>
						<Grid.Column width={isServiceComboboxVisible ? 7 : 14}>
							<Form.Field
								code={autorisation.code}
								control={CodeInput}
								label="Code"
								onChange={handleChangeAutorisationInput}
								required
							/>
						</Grid.Column>
						{isServiceComboboxVisible && (
							<Grid.Column width={7}>
								<Form.Field
									control={() => ServiceInput}
									label="Service utilisateur"
									required
								/>
							</Grid.Column>
						)}
					</Grid.Row>
				</Grid>
			</Form>
			<Form>
				<Form.Group widths="equal">
					<Form.Input
						data-testid="libelleInput"
						label="Libellé"
						name="libelle"
						onChange={handleChangeAutorisationInput}
						required
						value={autorisation.libelle}
					/>
					<Form.Field
						control={EtiquettesInput}
						dropdownProps={{
							multiple: false,
							noResultsMessage: 'Aucune catégorie trouvée',
							value: autorisation.identifiantEtiquette,
						}}
						label="Catégorie"
						onChange={handleChangeEtiquette}
						placeholder=""
						required
					/>
				</Form.Group>
				<Form.TextArea
					data-testid="descriptionTextArea"
					label="Description"
					name="description"
					onChange={handleChangeAutorisationInput}
					rows={3}
					value={autorisation.description}
				/>
				<Form.Group widths="equal">
					<Currency
						data-testid="montantAutorisationCurrency"
						label="Montant total de l'autorisation"
						name="montantAutorisation"
						onChange={handleChangeAutorisationMontant}
						required
						value={autorisation.montantAutorisation}
					/>
					<Form.Field
						control={Date}
						data-testid="dateDeliberationInput"
						fluid
						label="Date de délibération"
						name="dateVote"
						onChange={handleChangeDate}
						value={autorisation.dateVote}
					/>
				</Form.Group>
			</Form>
			<Divider />
			<Segment basic>
				<Grid>
					<Grid.Row>
						<Grid.Column width={4}>
							<Header
								content="Gestion des évènements"
								size="tiny"
								subheader="Décomposer l'autorisation en plusieurs phases/évènements"
							/>
						</Grid.Column>
						<Grid.Column textAlign="left" verticalAlign="middle">
							<Radio
								checked={hasEvenements}
								data-testid="hasEvenementsCheckbox"
								disabled={
									autorisation.composants?.length > 0 ||
									autorisation.financements?.length > 0
								}
								onChange={onChangeHasEvenements}
								toggle
								type="checkbox"
							/>
						</Grid.Column>
					</Grid.Row>
					{hasEvenements && (
						<>
							{autorisation.composants?.map(
								({ description, identifiant, libelle, montantEvenement }) => (
									<Grid.Row key={identifiant}>
										<Grid.Column width={4}>
											<Input
												data-testid={`libelleComposantInput${identifiant}`}
												fluid
												name="libelle"
												onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
													handleBlurComposantInput(e, identifiant)
												}
												placeholder="Nom de l'évènement"
												required
												value={libelle}
											/>
										</Grid.Column>
										<Grid.Column width={7}>
											<Input
												data-testid={`descriptionComposantInput${identifiant}`}
												fluid
												name="description"
												onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
													handleBlurComposantInput(e, identifiant)
												}
												placeholder="Description"
												required
												value={description}
											/>
										</Grid.Column>
										<Grid.Column width={4}>
											<Currency
												data-testid={`montantComposantInput${identifiant}`}
												fluid
												onBlur={(_e, montant: number) =>
													handleBlurComposantMontant(identifiant, montant)
												}
												required
												value={montantEvenement}
											/>
										</Grid.Column>
										<Grid.Column
											width={1}
											textAlign="center"
											verticalAlign="middle"
										>
											<Icon
												color="red"
												cursor="pointer"
												data-testid={`removeComposantButton${identifiant}`}
												iconSet="Lucide"
												name="Trash2"
												onClick={() => onRemoveComposant(identifiant)}
												size={16}
											/>
										</Grid.Column>
									</Grid.Row>
								),
							)}
							<Grid.Row>
								<Grid.Column>
									<Button
										basic
										content="Ajouter un évènement"
										data-testid="addComposantButton"
										icon="plus"
										onClick={onAddComposant}
										type="button"
									/>
								</Grid.Column>
							</Grid.Row>
						</>
					)}
				</Grid>
			</Segment>
		</>
	);
};

export default InformationsGenerales;
